import { useEffect, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from '@mui/material'
import { CheckCircle, Cancel } from '@mui/icons-material'
import { ApiService } from '@api/apiService'

interface ApprovalLookup {
  requestId: string
  adventureId: string
  adventureName: string
  sessionId: string
  workflowNodeId: string
  nodeLabel: string | null
  instructions: string | null
  status: number
  createdAt: string
  expiresAt: string | null
  requestingUserId: string
  requestingUserDisplayName: string
  isResolved: boolean
}

interface ModeratorApprovalScanDialogProps {
  /** Approval request id extracted from the scanned QR payload. */
  requestId: string | null
  onClose: () => void
}

/**
 * Modal that fires when a moderator scans a QR-mode approval token. Fetches
 * the request's preview info, then offers Approve / Reject buttons that hit
 * the existing /api/moderator-approvals/{id}/approve|deny endpoints.
 *
 * Renders nothing when requestId is null (caller controls open state by
 * setting/clearing requestId).
 */
export default function ModeratorApprovalScanDialog({ requestId, onClose }: ModeratorApprovalScanDialogProps) {
  const [loading, setLoading] = useState(false)
  const [lookup, setLookup] = useState<ApprovalLookup | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState<'approve' | 'deny' | null>(null)
  const [resolved, setResolved] = useState<'approved' | 'denied' | null>(null)
  const [rejectionReason, setRejectionReason] = useState('')

  useEffect(() => {
    if (!requestId) {
      setLookup(null)
      setError(null)
      setResolved(null)
      setRejectionReason('')
      return
    }
    let cancelled = false
    setLoading(true)
    setError(null)
    ApiService.get<ApprovalLookup>(`/api/moderator-approvals/${requestId}`)
      .then((data) => { if (!cancelled) setLookup(data) })
      .catch((e: any) => {
        if (cancelled) return
        const status = e?.response?.status
        if (status === 404) {
          setError("This approval token isn't valid for your account — only moderators of the originating adventure can approve.")
        } else {
          setError(e?.response?.data?.errorMessage || e?.message || 'Could not load approval request.')
        }
      })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [requestId])

  if (!requestId) return null

  const handleApprove = async () => {
    if (!lookup) return
    setSubmitting('approve')
    setError(null)
    try {
      await ApiService.post(`/api/moderator-approvals/${lookup.requestId}/approve`, { notes: null })
      setResolved('approved')
    } catch (e: any) {
      setError(e?.response?.data?.errorMessage || e?.message || 'Could not approve.')
    } finally {
      setSubmitting(null)
    }
  }

  const handleReject = async () => {
    if (!lookup) return
    setSubmitting('deny')
    setError(null)
    try {
      await ApiService.post(`/api/moderator-approvals/${lookup.requestId}/deny`, {
        reason: rejectionReason.trim() || null,
      })
      setResolved('denied')
    } catch (e: any) {
      setError(e?.response?.data?.errorMessage || e?.message || 'Could not deny.')
    } finally {
      setSubmitting(null)
    }
  }

  return (
    <Dialog open onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Moderator Approval</DialogTitle>
      <DialogContent>
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        )}

        {error && !loading && (
          <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
        )}

        {lookup && !loading && lookup.isResolved && (
          <Alert severity="info">This request has already been resolved.</Alert>
        )}

        {lookup && !loading && !lookup.isResolved && resolved === null && (
          <Box>
            <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
              {lookup.adventureName}
            </Typography>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              {lookup.nodeLabel || 'Moderator approval requested'}
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }}>
              Requested by <strong>{lookup.requestingUserDisplayName}</strong>
            </Typography>
            {lookup.instructions && (
              <Typography variant="body2" sx={{ mt: 1.5, color: 'text.primary' }}>
                {lookup.instructions}
              </Typography>
            )}
            <TextField
              fullWidth
              size="small"
              sx={{ mt: 2 }}
              label="Rejection reason (optional)"
              placeholder="Only sent if you tap Reject"
              value={rejectionReason}
              onChange={(e) => setRejectionReason(e.target.value)}
              multiline
              minRows={2}
            />
          </Box>
        )}

        {resolved === 'approved' && (
          <Alert severity="success" icon={<CheckCircle />}>Approved — the player will advance.</Alert>
        )}
        {resolved === 'denied' && (
          <Alert severity="warning" icon={<Cancel />}>Rejected — the player will take the rejection branch.</Alert>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        {resolved === null && lookup && !lookup.isResolved && (
          <>
            <Button
              variant="outlined"
              color="error"
              onClick={handleReject}
              disabled={!!submitting}
              startIcon={submitting === 'deny' ? <CircularProgress size={16} /> : <Cancel />}
            >
              Reject
            </Button>
            <Button
              variant="contained"
              color="success"
              onClick={handleApprove}
              disabled={!!submitting}
              startIcon={submitting === 'approve' ? <CircularProgress size={16} color="inherit" /> : <CheckCircle />}
            >
              Approve
            </Button>
          </>
        )}
        <Button onClick={onClose}>
          {resolved !== null ? 'Done' : 'Close'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
